import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { removeHtmlTags } from 'src/app/common/utils/string.extensions';
import { EditorChoice } from 'src/app/modules/shared/models/editor.choice';
import { VisitorEvent } from 'src/app/modules/shared/models/visitor.event';
import { VisitorPlace } from 'src/app/modules/shared/models/visitor.place';
import { Itinerary } from 'src/app/modules/trip-planner/models/itinerary';
import { TripPlannerService } from 'src/app/modules/trip-planner/services/trip.planner.service';
import { finalize, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { OnDestroy } from '@angular/core';
import { clone, isEmptyArray, isNullOrUndefined } from 'src/app/common/utils/object.extensions';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { select, Store } from '@ngrx/store';
import { IAppState, IFrontendSettings } from '../../../ngrx.stores/frontend.settings/states';
import { frontendSettingsSelector } from '../../../ngrx.stores/frontend.settings/selectors';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import { EditEventModalComponent } from '../edit.event.modal/edit.event.modal.component';
import { FavoriteCustomEvent } from '../../../models/favorite.custom.event';
import { FavoriteCustomPlace } from '../../../models/favorite.custom.place';
import { updateSettings } from '../../../ngrx.stores/frontend.settings/actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { KeywordService } from '../../../services/keyword.service';
import { EditPlaceModalComponent } from '../edit.place.modal/edit.place.modal.component';
import { EventTimeService } from 'src/app/modules/event-calendar/services/event.time.service';

@Component({
  selector: 'ig-hub-event-card',
  templateUrl: './hub.event.card.component.html',
  styleUrl: './hub.event.card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HubEventCardComponent implements OnDestroy, OnInit {
  @Input() item: VisitorEvent;
  @Input() hubName: string;
  @Input() editorChoiceText: EditorChoice;

  _customEvents: FavoriteCustomEvent[] = [];
  @Input() set customEvents(value: FavoriteCustomEvent[]) {
    this._customEvents = clone(value);
    this.cdr.markForCheck();
  }
  _customPlaces: FavoriteCustomPlace[] = [];
  @Input() set customPlaces(value: FavoriteCustomPlace[]) {
    this._customPlaces = clone(value);
    this.cdr.markForCheck();
  }

  // for highlight search keywords
  keywordSubscription: Subscription;
  keyword: string;
  @Input() searchFields: string[] = [];

  isSelected = false;
  menuIsOpen = false;
  isOperating = false;

  itineraries: Itinerary[];
  private destroy$ = new Subject<void>();

  ngRxStore = inject(Store<IAppState>);
  placeService = inject(VisitorPlaceService);
  frontendSettings: IFrontendSettings;
  modalService = inject(ModalService);
  cdr = inject(ChangeDetectorRef);
  dashboardService = inject(DashboardService);
  snackBar = inject(MatSnackBar);
  keywordService = inject(KeywordService);
  timeService = inject(EventTimeService);

  constructor() {}

  ngOnInit() {
    TripPlannerService.itinerariesSubject$
      .pipe(takeUntil(this.destroy$))
      .subscribe((itineraries) => {
        if (!isEmptyArray(itineraries)) {
          this.itineraries = itineraries;
          this.itineraryCache.clear();
          this.cdr.markForCheck();
        }
      });

    DashboardService.selectedItemIds$.pipe(takeUntil(this.destroy$)).subscribe((selectedEvents) => {
      this.isSelected = selectedEvents.some((e) => e === this.item._id);
      this.cdr.markForCheck();
    });

    DashboardService.itemStatus$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (this.isSelected) {
        this.item.isToBeExport = !isNullOrUndefined(status.isToBeExport)
          ? status.isToBeExport.find((x) => x.id === this.item._id)?.status
          : this.item.isToBeExport;

        this.item.isLocked = !isNullOrUndefined(status.isLocked)
          ? status.isLocked.find((x) => x.id === this.item._id)?.status
          : this.item.isLocked;

        this.item.isChoice = !isNullOrUndefined(status.isChoice)
          ? status.isChoice.find((x) => x.id === this.item._id)?.status
          : this.item.isChoice;

        this.itineraryCache.clear();
        this.cdr.markForCheck();
      }
    });

    this.ngRxStore
      .pipe(takeUntil(this.destroy$), select(frontendSettingsSelector))
      .subscribe((settings) => {
        if (settings) {
          this.frontendSettings = settings;
          this.cdr.markForCheck();
        }
      });

    // for highlight search keywords
    this.keywordSubscription = this.keywordService.keyword$
      .pipe(takeUntil(this.destroy$))
      .subscribe((k) => {
        if (!k) return;
        this.keyword = k;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isSearchName(): boolean {
    return this.searchFields.length === 0 || this.searchFields.includes('name');
  }

  get isSearchAddress() {
    return this.searchFields.includes('address');
  }

  get isSearchCategory() {
    return this.searchFields.includes('category');
  }

  approve() {
    this.isOperating = true;

    const approveMethods = {
      event: this.dashboardService.approveEvent.bind(this.dashboardService),
      place: this.dashboardService.approvePlace.bind(this.dashboardService),
    };

    const method = approveMethods[this.getItemType()] || approveMethods.place;

    method(this.hubName, this.item._id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isOperating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(() => {
        this.item.isApproved = true;
      });
  }

  unapprove() {
    this.isOperating = true;
    const unApproveMethods = {
      event: this.dashboardService.unApproveEvent.bind(this.dashboardService),
      place: this.dashboardService.unApprovePlace.bind(this.dashboardService),
    };

    const method = unApproveMethods[this.getItemType()] || unApproveMethods.place;

    method(this.hubName, this.item._id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isOperating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(() => {
        this.item.isApproved = false;
      });
  }

  onMenuOpened() {
    this.menuIsOpen = true;
  }

  onMenuClosed() {
    this.menuIsOpen = false;
  }

  getDisplayCategory(place: VisitorPlace) {
    return this.placeService.getDisplayCategory(place);
  }

  get isInAnyItinerary(): boolean {
    return this.itineraries?.some((itinerary) =>
      itinerary.days.some((day) => day.attractions.some((attr) => attr.feedId === this.item._id))
    );
  }

  private itineraryCache = new Map<string, boolean>();

  isInSpecificItinerary(itineraryId: string): boolean {
    if (!itineraryId) return false;

    if (this.itineraryCache.has(itineraryId)) {
      return this.itineraryCache.get(itineraryId);
    }

    const result = this.itineraries?.some(
      (itinerary) =>
        itinerary._id === itineraryId &&
        itinerary.days.some((day) => day.attractions.some((attr) => attr.feedId === this.item._id))
    );

    this.itineraryCache.set(itineraryId, result);
    return result;
  }

  isInCustomCategory() {
    if (this.getItemType() === 'event') {
      return this.frontendSettings?.customSettings?.customEvents?.some((x) =>
        x.events.includes(this.item._id)
      );
    }
    return this._customPlaces?.some((x) => x.places.includes(this.item._id));
  }

  isInSpecificCategory(categoryIndex: number) {
    if (this.getItemType() === 'event') {
      const category = this._customEvents[categoryIndex];
      if (!category) return false;
      return category.events.includes(this.item._id);
    } else {
      const category = this._customPlaces[categoryIndex];
      if (!category) return false;
      return category.places.includes(this.item._id);
    }
  }

  addToItinerary(itineraryId: string) {
    this.isOperating = true;
    this.dashboardService
      .addAttractionToTour(this.hubName, itineraryId, {
        feedId: this.item._id,
        feedType: this.getItemType(),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isOperating = false))
      )
      .subscribe(() => {
        this.itineraryCache.set(itineraryId, true);
        const itinerary = this.itineraries.find((itinerary) => itinerary._id === itineraryId);
        if (itinerary) {
          itinerary.days[0].attractions.push({
            feedId: this.item._id,
            feedType: this.getItemType(),
            order: itinerary.days[0].attractions.length,
          });
        }
        this.cdr.markForCheck();
      });
  }

  removeFromItinerary(itineraryId: string) {
    this.isOperating = true;
    this.dashboardService
      .removeAttractionFromTour(this.hubName, itineraryId, {
        feedId: this.item._id,
        feedType: this.getItemType(),
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isOperating = false))
      )
      .subscribe(() => {
        this.itineraryCache.set(itineraryId, false);
        const itinerary = this.itineraries.find((itinerary) => itinerary._id === itineraryId);
        if (itinerary) {
          for (const day of itinerary.days) {
            day.attractions = day.attractions.filter(
              (x) => x.feedId !== this.item._id && x.feedType === this.getItemType()
            );
          }
        }
        this.cdr.markForCheck();
      });
  }

  addToCategory(categoryIndex: number) {
    this.isOperating = true;
    if (this.getItemType() === 'event') {
      this.dashboardService
        .addEventToCustomBtn(this.hubName, this.item._id, categoryIndex)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isOperating = false))
        )
        .subscribe(() => {
          if (!this._customEvents[categoryIndex].events.includes(this.item._id)) {
            this._customEvents[categoryIndex].events.push(this.item._id);
          }
          this.ngRxStore.dispatch(
            updateSettings({
              customSettings: {
                ...this.frontendSettings.customSettings,
                customEvents: this._customEvents,
              },
            })
          );
          this.cdr.markForCheck();
        });
    } else {
      this.dashboardService
        .addPlaceToCustomBtn(this.hubName, this.item._id, categoryIndex)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isOperating = false))
        )
        .subscribe(() => {
          if (!this._customPlaces[categoryIndex].places.includes(this.item._id)) {
            this._customPlaces[categoryIndex].places.push(this.item._id);
          }
          this.ngRxStore.dispatch(
            updateSettings({
              customSettings: {
                ...this.frontendSettings.customSettings,
                customPlaces: this._customPlaces,
              },
            })
          );
          this.cdr.markForCheck();
        });
    }
  }

  removeFromCategory(categoryIndex: number) {
    if (this.getItemType() === 'event') {
      this.dashboardService
        .removeEventFromCustomBtn(this.hubName, this.item._id, categoryIndex)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isOperating = false))
        )
        .subscribe(() => {
          this._customEvents[categoryIndex].events = this._customEvents[
            categoryIndex
          ].events.filter((x) => x !== this.item._id);
          this.ngRxStore.dispatch(
            updateSettings({
              customSettings: {
                ...this.frontendSettings.customSettings,
                customEvents: this._customEvents,
              },
            })
          );
          this.cdr.markForCheck();
        });
    } else {
      this.dashboardService
        .removePlaceFromCustomBtn(this.hubName, this.item._id, categoryIndex)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.isOperating = false))
        )
        .subscribe(() => {
          this._customPlaces[categoryIndex].places = this._customPlaces[
            categoryIndex
          ].places.filter((x) => x !== this.item._id);
          this.ngRxStore.dispatch(
            updateSettings({
              customSettings: {
                ...this.frontendSettings.customSettings,
                customPlaces: this._customPlaces,
              },
            })
          );
          this.cdr.markForCheck();
        });
    }
  }

  selectItem() {
    const event = this.item as VisitorEvent;

    const isSelected = DashboardService.isSelected(event._id);

    if (isSelected) {
      DashboardService.removeSelectedItem(event._id);
      this.isSelected = false;
    } else {
      DashboardService.addSelectedItem(event._id);
      this.isSelected = true;
    }
  }

  isInItinerary(itineraryId?: string) {
    if (itineraryId) {
      console.log('checking itinerary');
      const itinerary = this.itineraries?.find((itinerary) => itinerary._id === itineraryId);
      if (itinerary) {
        return itinerary.days.some((day) =>
          day.attractions.some((attr) => attr.feedId === this.item._id)
        );
      }
      return false;
    }

    return this.itineraries?.some((itinerary) =>
      itinerary.days.some((day) => day.attractions.some((attr) => attr.feedId === this.item._id))
    );
  }

  getItemType() {
    return this.item.startTime ? 'event' : 'place';
  }

  getDisplaySocialName() {
    if (this.item.socialName) {
      if (this.item.socialName.includes('google')) return 'Google';
      if (this.item.socialName.includes('eventbrite')) return 'Eventbrite';
      if (this.item.socialName.includes('facebook')) return 'Facebook';
      return this.item.socialName;
    }
    if (this.item.isHubCreated) return 'Manual';
    if (this.item.isManuallyCreated) return 'Website Submitted';
    return '';
  }

  processDescription(description: string) {
    return removeHtmlTags(description);
  }

  onEdit() {
    const itemType = this.getItemType();
    if (itemType === 'place') {
      const modal = this.modalService.show({
        component: EditPlaceModalComponent,
        data: this.item,
        panelClass: ['ig-edit-modal', '!w-[1000px]'],
      });
      modal.afterClosed().subscribe((result) => {
        if (result) {
          console.log(result);
          this.item = result;
          this.cdr.detectChanges();
        }
      });
    } else {
      const modal = this.modalService.show({
        component: EditEventModalComponent,
        data: this.item,
        panelClass: ['ig-edit-modal', '!w-[1000px]'],
      });

      modal.afterClosed().subscribe((result) => {
        if (result) {
          this.timeService.processEventTimes(result);
          console.log(result);
          this.item = result;
          this.cdr.detectChanges();
        }
      });
    }
  }

  enlargeImage($event: Event) {
    if (this.isOperating) return;

    this.isOperating = true;

    this.dashboardService
      .resizeImageFromUnApprovedEvent(this.hubName, this.item._id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isOperating = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (result) => {
          if (result.success) {
            this.item.cover = {
              source: result.url,
              isLarge: true,
            };
          } else {
            this.snackBar.open('Something went wrong, please try again.', 'Close', {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['ig-error-snackbar'],
              duration: 3000,
            });
            this.isOperating = false;
          }
        },
        error: (error) => {
          this.isOperating = false;
          console.error('Error enlarging image', error);
        },
      });
  }

  get shouldShowEnlargeImage(): boolean {
    if (
      this.getItemType() !== 'event' ||
      !this.item ||
      !this.item.socialName ||
      !this.item.cover ||
      !this.item.cover.source ||
      this.item.cover.isLarge
    ) {
      return false;
    }

    // only show the enlarge image for newly pulled events
    return (
      this.item.socialName.includes('serp|google') &&
      !this.item.cover.isLarge &&
      new Date(this.item.createdAt).getTime() > new Date('2025-02-26').getTime()
    );
  }
}
